import FormControl from "@mui/material/FormControl/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText/FormHelperText";
import FormLabel from "@mui/material/FormLabel/FormLabel";
import Radio from "@mui/material/Radio/Radio";
import RadioGroup from "@mui/material/RadioGroup/RadioGroup";
import TextField from "@mui/material/TextField/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker/DatePicker";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import * as yup from "yup";
import { AppButton, PageHeader } from "../../../../components/ui";
import {
  LeadsService,
  ReferralCaseProgressViewModel,
} from "../../../../api";
import { errorInstructions } from "../shared/Constants";
import {
  startSpinner,
  stopSpinner,
} from "../../../../redux/actions/spinnerActions";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { NumericFormat } from "react-number-format";

const MySwal = withReactContent(Swal);

const schema = yup.object().shape({});

export default function ReferralCaseProgressForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const formik = useFormik<ReferralCaseProgressViewModel>({
    initialValues: {
      clientRetained: undefined,
      retainerSignedDate: null,
      pagaNoticeFiledDate: null,
      complaintFiledDate: null,
      complaintCourt: "",
      mediationScheduledDate: null,
      compelledToArbitrationDate: null,
      caseNumber: "",
      settlementAmount: null,
      paymentSentDate: null,
      checkNumber: "",
      checkAmount: null,
      notes: "",
      isDraft: false,
    },
    validationSchema: schema,
    onSubmit: (values) => handleSave(values, false),
  });

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    setValues,
  } = formik;

  useEffect(() => {
    if (!id) return;
    dispatch(startSpinner());
    LeadsService.getApiLeadsReferralCaseProgress(+id)
      .then((data) => {
        if (data) {
          setValues({
            ...data,
            complaintCourt: data.complaintCourt ?? "",
            caseNumber: data.caseNumber ?? "",
            checkNumber: data.checkNumber ?? "",
            notes: data.notes ?? "",
          });
        }
      })
      .finally(() => dispatch(stopSpinner()));
  }, [id]);

  const handleSave = (formValues: ReferralCaseProgressViewModel, isDraft: boolean) => {
    dispatch(startSpinner());
    LeadsService.putApiLeadsReferralCaseProgress(+id!, {
      ...formValues,
      isDraft,
    })
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: <p className="title">Case progress saved</p>,
        }).then(() => navigate(`/lead/${id}`));
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error saving case progress</p>,
          html: <div className="alert-text">{errorInstructions}</div>,
        });
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const showMilestones = values.clientRetained === true;

  return (
    <div className="row main-holder p-5">
      <div className="col-12 mb-4">
        <AppButton
          startIcon={<KeyboardArrowLeftIcon />}
          onClick={() => navigate(`/lead/${id}`)}
        >
          Back to lead
        </AppButton>
      </div>
      <div className="col-12">
        <PageHeader
          title="Case status"
          subtitle="Update litigation milestones for this lead — retainer, filings, court, mediation, and arbitration dates."
        />
      </div>

      <div className="col-12 col-lg-8">
        <h4 className="title mt-4 mb-3">Case milestones</h4>
        <div className="row">
          <div className="col-md-6 mt-3">
            <DatePicker
              label="Retainer signed"
              value={values.retainerSignedDate ? dayjs(values.retainerSignedDate) : null}
              onChange={(d) => setFieldValue("retainerSignedDate", d?.toISOString() ?? null)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </div>
          <div className="col-md-6 mt-3">
            <DatePicker
              label="PAGA notice filed"
              value={values.pagaNoticeFiledDate ? dayjs(values.pagaNoticeFiledDate) : null}
              onChange={(d) => setFieldValue("pagaNoticeFiledDate", d?.toISOString() ?? null)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </div>
          <div className="col-md-6 mt-3">
            <DatePicker
              label="Complaint filed"
              value={values.complaintFiledDate ? dayjs(values.complaintFiledDate) : null}
              onChange={(d) => setFieldValue("complaintFiledDate", d?.toISOString() ?? null)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </div>
          <div className="col-md-6 mt-3">
            <TextField
              fullWidth
              label="Complaint filed place"
              name="complaintCourt"
              value={values.complaintCourt ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. Los Angeles Superior Court"
            />
          </div>
          <div className="col-md-6 mt-3">
            <TextField
              fullWidth
              label="Case No"
              name="caseNumber"
              value={values.caseNumber ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="e.g. 25CU053736C"
            />
          </div>
          <div className="col-md-6 mt-3">
            <DatePicker
              label="Mediation scheduled for"
              value={values.mediationScheduledDate ? dayjs(values.mediationScheduledDate) : null}
              onChange={(d) => setFieldValue("mediationScheduledDate", d?.toISOString() ?? null)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </div>
          <div className="col-md-6 mt-3">
            <DatePicker
              label="Compelled to arbitration"
              value={values.compelledToArbitrationDate ? dayjs(values.compelledToArbitrationDate) : null}
              onChange={(d) => setFieldValue("compelledToArbitrationDate", d?.toISOString() ?? null)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </div>
        </div>

        <FormControl className="mt-5" error={!!errors.clientRetained && !!touched.clientRetained}>
          <FormLabel>Did you sign / retain this client?</FormLabel>
          <RadioGroup
            row
            name="clientRetained"
            value={
              values.clientRetained === true
                ? "yes"
                : values.clientRetained === false
                ? "no"
                : ""
            }
            onChange={(e) =>
              setFieldValue("clientRetained", e.target.value === "yes")
            }
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {!!errors.clientRetained && !!touched.clientRetained && (
            <FormHelperText>{errors.clientRetained}</FormHelperText>
          )}
        </FormControl>

        {showMilestones && (
          <>
            <h4 className="title mt-5 mb-3">Settlement</h4>
            <div className="row">
              <div className="col-md-6 mt-3">
                <NumericFormat
                  thousandSeparator=","
                  value={values.settlementAmount ?? ""}
                  onValueChange={(v) => setFieldValue("settlementAmount", v.floatValue ?? null)}
                  customInput={TextField}
                  label="Settlement amount"
                  fullWidth
                  InputProps={{ startAdornment: "$" }}
                />
              </div>
              <div className="col-md-6 mt-3">
                <DatePicker
                  label="Payment sent"
                  value={values.paymentSentDate ? dayjs(values.paymentSentDate) : null}
                  onChange={(d) => setFieldValue("paymentSentDate", d?.toISOString() ?? null)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </div>
              <div className="col-md-6 mt-3">
                <TextField
                  fullWidth
                  label="Check number"
                  name="checkNumber"
                  value={values.checkNumber ?? ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className="col-md-6 mt-3">
                <NumericFormat
                  thousandSeparator=","
                  value={values.checkAmount ?? ""}
                  onValueChange={(v) => setFieldValue("checkAmount", v.floatValue ?? null)}
                  customInput={TextField}
                  label="Check amount"
                  fullWidth
                  InputProps={{ startAdornment: "$" }}
                />
              </div>
            </div>
          </>
        )}

        <div className="row mt-4">
          <div className="col-12">
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Additional notes"
              name="notes"
              value={values.notes ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Any other case updates..."
            />
          </div>
        </div>

        <div className="row mt-5 button-holder">
          <AppButton
            variant="outlined"
            color="secondary"
            onClick={() => handleSave(values, true)}
          >
            Save draft
          </AppButton>
          <AppButton
            variant="contained"
            color="primary"
            onClick={() => formik.handleSubmit()}
          >
            Submit
          </AppButton>
        </div>
      </div>
    </div>
  );
}
